import { useEffect, useMemo, useState } from 'react';

import { type FieldValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useKeycloakContext } from '@wf/keycloak-axios-provider';
import { Mode } from '@wf/components';

import { rimmEntityToOption } from '../../rimm/utils/rimmEntityToOption';
import { type UseFormSetValue } from 'react-hook-form/dist/types/form';
import { NamesAndPartiesTabFields } from '../types/fieldEnums';
import { type IUserAttributes } from '../../../types';
import { LpcoRole } from '../../../constants';

import { type ICompanyRimmEntity, type TRimmEntityOption } from '../../rimm/data/rimmEntityTypes';
import { fetchRimmEntity, fetchRimmEntityByCodes } from '../../rimm/services';
import { RimmEntityType } from '../../rimm/data/rimmEntityEnums';
import getSelectValue from '../helpers/getSelectValue';

export function useCompanyAndDeclarantDetails(setValue: UseFormSetValue<FieldValues>, mode: Mode) {
  const dispatch = useDispatch();

  const keycloakContext = useKeycloakContext();
  const user = keycloakContext?.getUserData();
  const userAttributes: IUserAttributes = keycloakContext?.keycloak?.tokenParsed;
  const userRoles: LpcoRole[] = user?.roles?.lpco2_client;

  const [companyOptions, setCompanyOptions] = useState<TRimmEntityOption[RimmEntityType.company][] | null>(null);

  const companyCodes = useMemo(() => (userAttributes.TIN || '')?.split(':'), [userAttributes.TIN]);

  const declarantCode = userAttributes.DEC;

  const companyFieldsAreDisabled =
    userRoles.length === 1 &&
    userRoles.includes(LpcoRole.TRADER) &&
    companyCodes.length === 1 &&
    companyCodes[0] !== 'ALL';

  const declarantFieldAreDisabled =
    userRoles.length === 1 && userRoles.includes(LpcoRole.DECLARANT) && declarantCode !== 'ALL';

  useEffect(() => {
    // fetch specific company if there is only one companyCode in userAttributes
    // in this case companyCode field should be disabled
    if (companyFieldsAreDisabled && mode === Mode.create) {
      const getCompanyDetails = async () => {
        try {
          const {
            data: {
              resultList: [companyDetails],
            },
          } = await fetchRimmEntity({ entity: RimmEntityType.company, searchText: companyCodes[0] });

          if (companyDetails) {
            const { code, description, address1, address2, email, phoneNumber } = companyDetails as ICompanyRimmEntity;
            setValue(
              NamesAndPartiesTabFields.companyCode,
              getSelectValue({
                value: code,
                tagLabel: description,
              })
            );
            setValue(NamesAndPartiesTabFields.companyDescription, description);
            setValue(NamesAndPartiesTabFields.companyAddress, `${address1} ${address2}`);
            setValue(NamesAndPartiesTabFields.companyEmail, email);
            setValue(NamesAndPartiesTabFields.companyPhone, phoneNumber);
          }
        } catch (e) {
          console.error(e);
        }
      };

      void getCompanyDetails();
    }
  }, [companyCodes, companyFieldsAreDisabled, mode, setValue]);

  useEffect(() => {
    // fetch companies data if there are more than one company codes in userAttributes
    // in this case it should be passed to component as select options
    if (companyCodes.length > 1) {
      const getCompanyList = async () => {
        try {
          const {
            data: { resultList },
          } = await fetchRimmEntityByCodes({
            entity: RimmEntityType.company,
            codes: companyCodes,
          });
          const options = rimmEntityToOption(resultList, true);
          setCompanyOptions(options as TRimmEntityOption[RimmEntityType.company][]);
        } catch (e) {
          console.error(e);
        }
      };

      void getCompanyList();
    }
  }, [companyCodes, dispatch]);

  useEffect(() => {
    if (declarantFieldAreDisabled && mode === Mode.create) {
      const getDeclarantDetails = async () => {
        try {
          const {
            data: {
              resultList: [declarantDetails],
            },
          } = await fetchRimmEntity({ entity: RimmEntityType.declarant, searchText: declarantCode });

          if (declarantDetails) {
            const { code, description } = declarantDetails;
            setValue(NamesAndPartiesTabFields.declarantCode, code);
            setValue(NamesAndPartiesTabFields.declarantName, description);
          }
        } catch (e) {
          console.error(e);
        }
      };

      void getDeclarantDetails();
    }
  }, [declarantCode, declarantFieldAreDisabled, mode, setValue]);

  return { companyFieldsAreDisabled, declarantFieldAreDisabled, companyOptions };
}
